(ns admin.components.forms.activity
  (:require
   [re-frame.core :as re-frame]
   [reagent.core :as reagent]
   [reagent-form.core :as rf]
   [reagent-form.validators :as v]
   [reagent-form.transformers :as t]
   [reagent-form.utils :as rfu]
   [clojure.string :as str]

   [kyber.components.base.button :as button]
   [kyber.components.base.text :as text]
   [kyber.components.base.icons :as icon]
   [kyber.utils :as u]
   [kyber.styles.core :refer [gs styles->classes]]
   [kyber.lib.kyber-db :as kyber-db]

   [admin.router.utils :refer [admin-path]]
   [admin.events.activities :as >activities]
   [admin.constants :as c]
   [admin.components.forms.fields.tag-collections :as tag-collections]
   [admin.components.composite.resource-uploader :as resource-uploader]
   [admin.components.forms.fields.toggle :as toggle]
   [admin.components.forms.fields.completion-threshold :as completion-threshold]
   [admin.components.forms.fields.supplemental :as supplemental]
   [admin.components.composite.crud-container :as crud-container]))

(def content-container-styles
  {:display :flex
   :justify-content :space-between
   :align-items :center
   :padding (gs [:spacing :p8])
   :border-radius (gs [:radius :r7])
   :margin-bottom (gs [:spacing :p20])
   :border (str "1px solid " (gs [:colors :gray-scale-300]))})

(def classes
  (styles->classes
   {:form-container
    {:max-width (str c/page-max-width "px")
     :margin [["auto" (gs [:spacing :p20])]]
     :padding (gs [:spacing :p20])
     :background-color (gs [:colors :true-white])}

    :split-container
    {:display :flex
     :justify-content :space-between
     :align-items :flex-start}

    :items-center
    {:display :flex
     :justify-content :space-between
     :align-items :center}

    :left-section
    {:width "50%"
     :margin-right (gs [:spacing :p20])}

    :right-section
    {:position :relative
     :display :flex
     :flex-direction :column
     :width "50%"
     :padding (gs [:spacing :p20])
     :border-left (str "1px solid" (gs [:colors :gray-scale-100]))}

    :content-container
    content-container-styles

    :admin-content-container
    (merge content-container-styles
           {:border (str "1px solid " (gs [:colors :error-500]))
            :background-color "rgba(255,0,0,0.1)"})

    :optional-container
    (-> content-container-styles
        (dissoc :display :justify-content :align-items)
        (merge {:border (str "1px solid " (gs [:colors :error-500]))
                :background-color "rgba(255,0,0,0.1)"
                "& .reagent-form-field"
                {:padding 0
                 :margin-bottom 0}
                "& .reagent-form-field-error"
                {:position :relative}}))

    :no-form-field-margins
    {"& div.reagent-form-field"
     {:padding 0
      :margin 0}
     "& .reagent-form-field-error"
     {:position :relative}}

    :admin-field-label
    {:color (gs [:colors :error-500])}

    :admin-input-container
    {:display :flex
     :min-width "100px"
     :margin-left (gs [:spacing :p32])
     :padding-left (gs [:spacing :p20])
     :border-left (str "1px solid " (gs [:colors :gray-scale-300]))}

    :activity-file-uploader-container
    {:display :flex
     :flex-direction :column
     :margin-bottom (gs [:spacing :p20])
     :padding-bottom (gs [:spacing :p20])
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))}

    :uploader-container
    {:margin-top (gs [:spacing :p40])}

    :image-wrapper
    {:display :flex
     :justify-content :center
     :align-items :center
     :width "100%"}

    :preview-image
    {:max-width "100%"}

    :disabled-files-container
    {:background-color "rgba(255,255,255,0.6)"
     :top 0
     :left 0
     :z-index 1
     :width "100%"
     :height "100%"
     :position :absolute}

    :save-activity-mesage
    {:text-align :center}}))

(defn- pause-and-close
  [show-form]
  (js/setTimeout
   #(reset! show-form false)
   1000))

(defn- render-activity-file-uploader
  [_]
  (let [upload-new (reagent/atom false)]
    (fn [{:keys [activity
                public
                file-key
                title
                description
                accepted-file-types
                disabled
                open-in-tab
                label
                success-message]
         :or {public false
              label "Replace Image"}}]
      (let [{activity-uuid :activity/uuid} activity
            src (file-key activity)]
        [:div {:class (:activity-file-uploader-container classes)}
         [text/caption-30 :label title]

         [text/caption-20 :p
          {:style {:margin-bottom (gs [:spacing :p12])}}
          description]

         (if (true? @upload-new)
           [resource-uploader/render-inline-uploader
            {:activity-uuid activity-uuid
             :on-success (fn [{:resource/keys [viewer-path]}]
                           (re-frame/dispatch
                            [::>activities/update-activity-v2
                             (assoc {:activity/uuid activity-uuid} file-key viewer-path)
                             {:on-success-toast success-message
                              :on-success #(pause-and-close upload-new)}]))
             :on-cancel #(reset! upload-new false)
             :public-access public
             :open-in-tab open-in-tab
             :accepted-file-types accepted-file-types}]
           [:div
            (if (some? src)
              (if (u/is-image? src)
                [:div {:class (:image-wrapper classes)}
                 [:img {:src src
                        :class (:preview-image classes)}]]
                [:div
                 [icon/icon-massive :file-alt]
                 [text/caption-20 :p (u/file-path->file-name src)]])
              [text/title-10 :p "No file uploaded"])
            [button/primary-button
             {:style {:margin-top (gs [:spacing :p20])}
              :on-click #(reset! upload-new true)
              :disabled disabled}
             label]])]))))

(defn- render-activity-files
  [{{activity-uuid :activity/uuid} :activity}]
  (let [activity (re-frame/subscribe [::kyber-db/get-record {:model :activity
                                                             :ident activity-uuid}])]
    (fn [{:keys [is-new]}]
      (let [{:activity/keys [external-activity-link cme-front-matter-enabled]} @activity]
        [:div
         (when (empty? external-activity-link)
           [render-activity-file-uploader
            {:activity @activity
             :file-key :activity/poster
             :title "Poster Image"
             :public true
             :disabled is-new
             :accepted-file-types #{:image}
             :success-message "Poster Image Updated"
             :description (str "The poster image is displayed to learners "
                               "when they first view or return to the Activity.")}])

         [render-activity-file-uploader
          {:activity @activity
           :file-key :activity/tile
           :title "Curriculum Landing Page Tile"
           :public true
           :disabled is-new
           :accepted-file-types #{:image}
           :success-message "Curriculum Landing Page Tile Updated"
           :description (str "The curriculum landing page image is displayed in "
                             "the activity tile on the learner and admin "
                             "curriculum landing pages.")}]

         (when (and (true? cme-front-matter-enabled)
                    (empty? external-activity-link))
           [render-activity-file-uploader
            {:activity @activity
             :file-key :activity/cme-front-matter-url
             :title "Front Matter"
             :public true
             :open-in-tab true
             :disabled is-new
             :accepted-file-types #{:file :image :video}
             :label "Replace Front Matter"
             :success-message "Front Matter Updated"
             :description (str "Front matter is content presented to the learner "
                               "before they begin the activity.")}])]))))

(defn- activity-title [plo-type is-new]
  (when (some? plo-type)
    [text/title-20 :p {}
     (str (if is-new "New " "Edit ")
          (-> plo-type
              (u/plo->label)
              (str/capitalize))
          "-Based Activity")]))

(defn- tag->tag-description [tag]
  (str "When enabled, this Activity will only be visible to Learners belonging to the following "
       tag
       "."))

(defn- set-supplemental-tags
  [is-supplemental? supplemental-tags valid-tags]
  (into {}
        (map
         (fn [[{tag-group-uuid :content-provider.tag-group/uuid
                :as tag-group} tags]]
           (let [supplemental-tag-uuids
                 (as-> supplemental-tags $
                   (get $ tag-group-uuid)
                   (:selected $)
                   (map :content-provider.tag/uuid $)
                   (set $))]

             {tag-group
              (mapv (fn [tag]
                      (if (and is-supplemental?
                               (get supplemental-tag-uuids (:content-provider.tag/uuid tag)))
                        (assoc tag :content-provider.tag/is-supplemental? true)
                        (dissoc tag :content-provider.tag/is-supplemental?)))
                    tags)}))
         valid-tags)))

(defn render
  [{:keys [close resource-name context plo-type]} activity]
  (let [{:keys [curriculum-uuid]}
        context

        event-key
        (if (some? activity)
          ::>activities/update-activity-v2
          ::>activities/create-activity-v2)

        delete-event-key
        ::>activities/delete-activity-v2

        is-submitting
        (re-frame/subscribe [:request/is-dispatching event-key])

        is-new
        (nil? activity)

        can-edit-advanced
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-edit-advanced
                             {:curriculum-uuid curriculum-uuid}])

        can-delete
        (re-frame/subscribe [:authentication/has-access?
                             :activity/can-delete
                             {:curriculum-uuid curriculum-uuid}])

        curriculum
        (re-frame/subscribe [::kyber-db/get-record {:model :curriculum
                                                    :ident curriculum-uuid
                                                    :query [:curriculum/valid-tags
                                                            :curriculum/intro-activity
                                                            :curriculum/compliance-enabled]}])]
    (fn [_ {activity-uuid :activity/uuid
            activity-name :activity/name
            :activity/keys [allow-disabling-of-cbqs
                            allow-unmatched-graded-questions
                            published
                            ai-enabled
                            description
                            future-scrubbing-enabled
                            note-taking-enabled
                            is-supplemental?
                            completion-threshold
                            completion-threshold-type
                            cme-front-matter-enabled
                            show-all-moments
                            external-activity-link
                            primary-learning-object
                            valid-tags]
            :or {note-taking-enabled true}
            :as activity}]
      (let [plo-type (or (:activity/plo-type activity) plo-type)
            plo-length (when (false? is-new)
                         (case plo-type
                           "video" (:resource/duration primary-learning-object)
                           "pdf" (:resource/page-count primary-learning-object)
                           nil))

            {{intro-activity-uuid :activity/uuid} :curriculum/intro-activity
             curriculum-valid-tags :curriculum/valid-tags
             curriculum-compliance-enabled :curriculum/compliance-enabled}
            @curriculum
            form-state (atom nil)]
        [:div {:class (:form-container classes)}
         [activity-title plo-type is-new]
         [:div {:class (:split-container classes)}
          [:div {:class (:left-section classes)}
           [rf/form {:id event-key
                     :is-submitting is-submitting
                     :on-initialized #(reset! form-state (:form-state %))}
            [:form
             {:rf/form
              {:on-submit
               #(let [{:activity/keys [external-activity-link
                                       completion-threshold
                                       is-supplemental?]
                       :keys [supplemental-tags]} %]

                  (re-frame/dispatch
                   [event-key
                    (cond-> (-> %
                                (dissoc :activity/completion-threshold
                                        :supplemental-tags)
                                (update :activity/valid-tags (partial set-supplemental-tags
                                                                      is-supplemental?
                                                                      supplemental-tags)))
                      (some? (:value completion-threshold))
                      (assoc :activity/completion-threshold (:value completion-threshold))

                      (some? (:type completion-threshold))
                      (assoc :activity/completion-threshold-type (:type completion-threshold))

                      (false? is-new)
                      (assoc :activity/uuid activity-uuid)

                      (true? is-new)
                      (assoc :curriculum/uuid curriculum-uuid
                             :activity/plo-type plo-type)

                      (empty? external-activity-link)
                      (dissoc :activity/external-activity-link))
                    (cond-> {:response-keys [:activity/allow-disabling-of-cbqs
                                             :activity/allow-unmatched-graded-questions
                                             :activity/cme-front-matter-enabled
                                             :activity/completion-threshold
                                             :activity/completion-threshold-type
                                             :activity/compliance-enabled
                                             :activity/description
                                             :activity/future-scrubbing-enabled
                                             :activity/is-supplemental?
                                             :activity/name
                                             :activity/note-taking-enabled
                                             :activity/published
                                             :activity/ai-enabled
                                             :activity/show-all-moments
                                             :activity/uuid
                                             {:activity/valid-tags
                                              [:content-provider.tag-group/uuid
                                               {:content-provider.tag-group/values
                                                [:content-provider.tag/uuid
                                                 :content-provider.tag/name
                                                 :content-provider.tag/is-supplemental?]}]}]
                             :on-success-toast (if is-new
                                                 "Activity created."
                                                 "Activity updated.")
                             :on-failure-toast (if is-new
                                                 "Something went wrong creating this activity."
                                                 "Something went wrong updating this activity.")}
                      is-new
                      (assoc :on-success (fn [{activity-uuid :activity/uuid}]
                                           (re-frame/dispatch
                                            [:router/nav-to
                                             (admin-path :activity-overview
                                                         :curriculum-uuid curriculum-uuid
                                                         :activity-uuid activity-uuid)]))))]))}}

             (rf/input
              (cond-> {:field-key :activity/name
                       :id :name
                       :type :text
                       :label "Activity Title"
                       :validators [{:validator v/required
                                     :message "Required"}]
                       :transformers [t/trim]}
                (some? activity-name)
                (assoc :default-value activity-name)))

             (rf/textarea
              (cond-> {:field-key :activity/description
                       :id :description
                       :label "Short Description"
                       :validators [{:validator v/required
                                     :message "Required"}]
                       :transformers [t/trim]}
                (some? description)
                (assoc :default-value description)))

             (when-not (true? is-new)
               [:div {:class (:content-container classes)
                      :style {:margin-top (gs [:spacing :p32])}}
                [:div
                 [text/caption-30 :label "Activity Published?"]
                 [text/caption-20 :p
                  (str "When the activity is published, learners will "
                       "be able to view the activity and engage with it. "
                       "If the activity is still under development it should "
                       "remain in draft mode to prevent learners from "
                       "accessing it.")]]
                [:div {:class (:admin-input-container classes)}
                 (rf/custom-field
                  {:field-key :activity/published
                   :id :published
                   :on-text "Yes"
                   :off-text "No"
                   :render toggle/render
                   :default-value published})]])

             ;; When there are tag-groups set on the curriculum, display them
             ;; at the activity level.
             ;;
             ;; When the activity that is being edited is an intro activity,
             ;; do NOT show the tag groups
             (when (and (not= activity-uuid intro-activity-uuid)
                        (seq (mapcat :content-provider.tag-group/values curriculum-valid-tags)))
               (rf/custom-field
                {:field-key :activity/valid-tags
                 :render tag-collections/render
                 :validators (mapv
                              (fn [tag-group]
                                {:validator
                                 #(let [{:keys [enabled selected]}
                                        (get % (:content-provider.tag-group/uuid tag-group))]
                                    (not (and enabled (empty? selected))))
                                 :message (str (:content-provider.tag-group/name tag-group)
                                               " can't be empty")})
                              curriculum-valid-tags)
                 :default-value (tag-collections/tag-groups->tag-collection-value
                                 {:available-tag-groups curriculum-valid-tags
                                  :selected-tag-groups valid-tags
                                  :allow-collection-toggling true
                                  :description-fn tag->tag-description
                                  :label-fn str/capitalize})}))

             (when (and (true? @can-edit-advanced)
                        (or (true? is-new)
                            (seq external-activity-link)))
               [:div {:style {:margin-top (gs [:spacing :p32])}
                      :class (:admin-content-container classes)}
                [:div
                 [text/caption-30 :label
                  {:class (:admin-field-label classes)}
                  "Configure as an External Activity"]
                 [text/caption-20 :p
                  (str "By configuring the activity as an external activity, "
                       "learners will be redirected to activities hosted "
                       "outside of the Ready platform.")]

                 (when (true? is-new)
                   [text/caption-30 :p
                    {:style {:color (gs [:colors :error-500])}}
                    (str "NOTE: Leave this field BLANK if you are setting up a "
                         "standard Ready activity.")])
                 (rf/input
                  (cond-> {:field-key :activity/external-activity-link
                           :id :external-activity-link
                           :type :text
                           :label "External URL"
                           :validators (cond-> []
                                         (false? is-new)
                                         (conj {:validator v/required
                                                :message "Required"}))
                           :transformers [t/trim]}
                    (some? external-activity-link)
                    (assoc :default-value external-activity-link)))]
                [:div {:class (:admin-input-container classes)}]])

             (when (or (true? is-new)
                       (empty? external-activity-link))
               [:<>
                (when (= "video" plo-type)
                  [:div {:class (:content-container classes)
                         :style {:margin-top (gs [:spacing :p32])}}
                   [:div
                    [text/caption-30 :label "Future Scrubbing Enabled?"]
                    [text/caption-20 :p
                     (str "Future scrubbing allows learners to scrub forward in "
                          "the video to sections of the video that they have "
                          "not yet encountered. If future scrubbing is "
                          "disabled, learners will still be able to scrub to "
                          "viewed sections of the video for review, but will "
                          "not be able to scrub to unviewed sections.")]]
                   [:div {:class (:admin-input-container classes)}
                    (rf/custom-field
                     {:field-key :activity/future-scrubbing-enabled
                      :id :future-scrubbing-enabled
                      :render toggle/render
                      :on-text "Yes"
                      :off-text "No"
                      :default-value (if (some? future-scrubbing-enabled)
                                       future-scrubbing-enabled
                                       true)})]])

                (when (false? is-new)
                  (rf/custom-field
                   {:field-key :activity/completion-threshold
                    :id :completion-threshold
                    :render completion-threshold/render
                    :plo-type plo-type
                    :default-value {:type (or completion-threshold-type :none)
                                    :value completion-threshold}
                    :plo-length plo-length}))

                (when @can-edit-advanced
                  [:div {:style {:margin-top (gs [:spacing :p32])}}
                   [:div {:style {:margin-top (gs [:spacing :p32])}}

                    (when (= "pdf" plo-type)
                      [:div {:class (:admin-content-container classes)
                             :style {:margin-top (gs [:spacing :p32])}}
                       [:div
                        [text/caption-30 :label
                         {:class (:admin-field-label classes)}
                         "Use AI?"]
                        [text/caption-20 :p
                         (str "When AI is enabled, administrators can use the ChatGPT integration to generate 
                               Learning Objectives, Assessment Questions, and Answer Choices from text based 
                               Learning Objects.")]]
                       [:div {:class (:admin-input-container classes)}
                        (rf/custom-field
                         {:field-key :activity/ai-enabled
                          :id :ai-enabled
                          :on-text "Yes"
                          :off-text "No"
                          :render toggle/render
                          :default-value (if (some? ai-enabled)
                                           ai-enabled
                                           false)})]])

                    [:div {:class (:optional-container classes)}
                     [:div {:class (:items-center classes)}
                      [:div
                       [text/caption-30 :label
                        {:class (:admin-field-label classes)}
                        "Optional Activity?"]
                       [text/caption-20 :p
                        (str "When enabled, this Activity will be presented to the learner, 
                          but they are not required to take it.")]]
                      [:div {:class (:admin-input-container classes)}
                       (rf/custom-field
                        {:field-key :activity/is-supplemental?
                         :id :supplemental-activity
                         :render toggle/render
                         :on-change #(if (false? %)
                                       (rfu/hide-field! @form-state :supplemental-tags)
                                       (rfu/show-field! @form-state :supplemental-tags))
                         :disabled (and (some? activity-uuid)
                                        (= activity-uuid intro-activity-uuid))
                         :disabled-message "Introduction activities cannot be optional."
                         :on-text "Yes"
                         :off-text "No"
                         :default-value (boolean is-supplemental?)})]]
                     (rf/custom-field
                      {:field-key :supplemental-tags
                       :id :supplemental-tags
                       :render supplemental/render
                       :validators [{:validator
                                     (fn [data]
                                       (->> data
                                            (remove (fn [[tag-group-uuid {:keys [selected]}]]
                                                      (let [tg (get-in @@form-state
                                                                       [:activity/valid-tags
                                                                        :data
                                                                        tag-group-uuid])]
                                                        (or (not (:enabled tg))
                                                            (and (:enabled tg)
                                                                 (seq selected))))))
                                            empty?))
                                     :message "Optional lists can't be empty"}]
                       :default-value {}})]]

                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Front Matter Enabled?"]
                     [text/caption-20 :p
                      (str "By enabling front matter, a link will be available "
                           "for learner to click to preview material about the "
                           "activity before starting it. When enabled, partner "
                           "admins will be able to manage the front matter "
                           "content.")]]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/cme-front-matter-enabled
                       :id :published
                       :render toggle/render
                       :on-text "Yes"
                       :off-text "No"
                       :default-value (if (some? cme-front-matter-enabled)
                                        cme-front-matter-enabled
                                        false)})]]

                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Note Taking Enabled?"]
                     [text/caption-20 :p
                      (str "Allows learners to take notes when working through "
                           "an activity.")]]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/note-taking-enabled
                       :id :note-taking-enabled
                       :render toggle/render
                       :on-text "Yes"
                       :off-text "No"
                       :default-value (boolean note-taking-enabled)})]]

                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Show Moments Before Viewed?"]
                     [text/caption-20 :p
                      (str "When enabled, learners will be able to see all "
                           "moments regardless of how much of the " (u/plo->label plo-type) " they "
                           "have viewed.")]]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/show-all-moments
                       :id :show-all-moments
                       :render toggle/render
                       :on-text "Yes"
                       :off-text "No"
                       :default-value (boolean show-all-moments)})]]

                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Disable confidence based questions?"]
                     [text/caption-20 :p
                      (str "When enabled, content authors will have the option "
                           "to turn on and off confidence for each question.")]]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/allow-disabling-of-cbqs
                       :id :allow-disabling-of-cbqs
                       :render toggle/render
                       :on-text "Yes"
                       :off-text "No"
                       :default-value (boolean allow-disabling-of-cbqs)})]]

                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Allow unmatched graded questions?"]
                     [text/caption-20 :p
                      (str "WARNING: Allowing unmatched graded questions can "
                           "result in unintended or confusing results when "
                           "interpreting data for the activity. Only enable "
                           "if absolutely necessary.")]]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/allow-unmatched-graded-questions
                       :id :allow-unmatched-graded-questions
                       :render toggle/render
                       :on-text "Yes"
                       :off-text "No"
                       :default-value (boolean
                                       allow-unmatched-graded-questions)})]]
                   
                   [:div {:class (:admin-content-container classes)}
                    [:div
                     [text/caption-30 :label
                      {:class (:admin-field-label classes)}
                      "Compliance Enabled"]
                     [text/caption-20 :p
                      (str "When enabled, this activity will be marked as compliant. "
                           "This setting cannot be disabled once enabled. "
                           "Note: Activity compliance can only be enabled when curriculum compliance is enabled.")]
                     (when (or (:activity/compliance-enabled activity)
                               curriculum-compliance-enabled)
                       [text/caption-30 :p
                        "✓ Compliance is currently enabled for this activity."])]
                    [:div {:class (:admin-input-container classes)}
                     (rf/custom-field
                      {:field-key :activity/compliance-enabled
                       :id :compliance-enabled
                       :render toggle/render
                       :disabled true
                       :validators []
                       :transformers []
                       :default-value (boolean (or (:activity/compliance-enabled activity)
                                                   curriculum-compliance-enabled))})]]])])

             (crud-container/ctas
              (cond-> {:resource-name resource-name
                       :is-new is-new
                       :cancel-button-copy (if is-new "Cancel" "Close")}
                (some? close)
                (assoc :close close)

                (and @can-delete
                     (some? activity))
                (assoc :on-delete
                       (fn []
                         (re-frame/dispatch
                          [:notifier/create-alert
                           {:title "Remove activity?"
                            :message "Are you sure you want to remove this activity?"
                            :confirm-text "Remove activity"
                            :deny-text "Cancel"
                            :confirm-action
                            (fn []
                              (re-frame/dispatch
                               [delete-event-key
                                {:activity/uuid activity-uuid :curriculum/uuid curriculum-uuid}
                                {:on-success-toast "Activity removed."
                                 :on-success
                                 #(re-frame/dispatch
                                   [:router/nav-to
                                    (admin-path :curriculum-activities
                                                :curriculum-uuid curriculum-uuid)])}]))}])))))]]]
          [:div {:class (:right-section classes)}
           (when (true? is-new)
             [:div {:class (:disabled-files-container classes)}
              [text/caption-30 :p
               {:class (:save-activity-mesage classes)}
               "Save the activity before adding activity assets"]])

           [render-activity-files
            {:is-new is-new
             :activity activity}]]]]))))
